/**
 * CommandPalette — Cmd/Ctrl+K opens a fuzzy-searchable list of
 * editor actions (load preset, change paper size, undo/redo, export
 * PDF, toggle terminals, toggle dark mode, etc).
 *
 * It's a force multiplier for any user past beginner stage and the
 * single biggest "how did this become a real tool" UX upgrade we
 * can ship in an hour. Inspired by Linear, Slack, VS Code.
 *
 * Implementation notes:
 *   - Lives at the App root, not inside Canvas, so it overlays the
 *     whole shell (including the right panel).
 *   - Action list is built from props passed by App so the palette
 *     stays unaware of how each action is implemented.
 *   - "Fuzzy" here is just substring + token match, ranked by how
 *     early the match starts. Good enough for ~30 actions; if we
 *     ever ship 200+ we'd swap to fuse.js.
 *   - Arrow keys navigate; Enter executes; Esc closes.
 */
import {
  useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';

export interface Command {
  /** Stable id, used for React key + persistence if we ever
   *  want recents/pinned. */
  id: string;
  /** Visible title — also the primary search target. */
  title: string;
  /** Optional sub-title (rendered below title or as group hint). */
  description?: string;
  /** Section label, shown as a faint pre-label on the row. */
  section?: string;
  /** Optional keyboard shortcut hint to render on the right. */
  shortcut?: string;
  /** Hidden tags that boost search matching without showing in UI. */
  keywords?: string[];
  /** Optional left-aligned icon. */
  icon?: ReactNode;
  /** Run this command. The palette closes itself before calling. */
  run: () => void;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: Command[];
}

export function CommandPalette({ open, onOpenChange, commands }: Props) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset query + active index every time the palette opens. Without
  // this the previous search lingers and the user has to clear it
  // before typing — minor but annoying.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // setTimeout 0 to wait for focus to be allowed.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Score each command. Returns { cmd, score } or null when no
  // match. Score is roughly "earlier hit + multi-token = better".
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // No query — show everything in the order it was registered,
      // grouped by section.
      return commands.map((c, i) => ({ cmd: c, score: 1000 - i }));
    }
    const tokens = q.split(/\s+/);
    const out: Array<{ cmd: Command; score: number }> = [];
    for (const cmd of commands) {
      const hay = [
        cmd.title,
        cmd.description ?? '',
        cmd.section ?? '',
        ...(cmd.keywords ?? []),
      ]
        .join(' ')
        .toLowerCase();

      // Every token must appear somewhere. Score = sum of
      // earliest-occurrence positions (lower = better → flip sign
      // to keep "higher score = better").
      let totalPos = 0;
      let allHit = true;
      for (const t of tokens) {
        const pos = hay.indexOf(t);
        if (pos === -1) { allHit = false; break; }
        totalPos += pos;
      }
      if (!allHit) continue;
      // Prefer hits in the title over hits in description/keywords.
      const titleBoost = cmd.title.toLowerCase().includes(q) ? 200 : 0;
      out.push({ cmd, score: 1000 - totalPos + titleBoost });
    }
    out.sort((a, b) => b.score - a.score);
    return out;
  }, [query, commands]);

  // Keep activeIdx in range when matches change.
  useEffect(() => {
    if (activeIdx >= matches.length) setActiveIdx(0);
  }, [matches, activeIdx]);

  // Scroll active row into view when navigating with arrow keys.
  useEffect(() => {
    const container = listRef.current;
    if (!container) return;
    const row = container.querySelector<HTMLDivElement>(
      `[data-cmdk-idx="${activeIdx}"]`
    );
    row?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, matches.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const m = matches[activeIdx];
      if (!m) return;
      onOpenChange(false);
      m.cmd.run();
      return;
    }
  };

  return (
    <div
      className="cmdk-backdrop"
      onMouseDown={(e) => {
        // Click outside the card closes. Inside the card, let
        // events bubble normally.
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="cmdk-card" onKeyDown={onKeyDown}>
        <input
          ref={inputRef}
          className="cmdk-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type a command, or search… (Esc to close)"
          autoComplete="off"
          spellCheck={false}
        />
        <div className="cmdk-list" ref={listRef}>
          {matches.length === 0 ? (
            <div className="cmdk-empty">No matching commands</div>
          ) : (
            matches.map((m, i) => (
              <div
                key={m.cmd.id}
                data-cmdk-idx={i}
                className={`cmdk-item${i === activeIdx ? ' active' : ''}`}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => e.preventDefault() /* keep input focus */}
                onClick={() => {
                  onOpenChange(false);
                  m.cmd.run();
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                  {m.cmd.icon && (
                    <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                      {m.cmd.icon}
                    </span>
                  )}
                  {m.cmd.section && (
                    <span className="cmdk-item-section">{m.cmd.section}</span>
                  )}
                  <span
                    className="cmdk-item-title"
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.cmd.title}
                    {m.cmd.description && (
                      <span
                        style={{
                          color: 'var(--text-faint)',
                          fontSize: 'var(--fs-sm)',
                          marginLeft: 8,
                        }}
                      >
                        {m.cmd.description}
                      </span>
                    )}
                  </span>
                </div>
                {m.cmd.shortcut && (
                  <span className="cmdk-item-shortcut">{m.cmd.shortcut}</span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
